import { useEffect, useRef, useState } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useAsyncResponse } from 'Hooks/useAsync';
import { getPaymentTabTotalAmount, PaymentTabPricesDto } from 'Services/Api/_HumanWritten/TransactionService';

/**
 * The payment step's total, shared by the amount shown on the payment form and the booking wizard's
 * `add_payment_info` event so the two read one request.
 *
 * Kept only while something is watching it (`gcTime: 0`): two readers mounted together share the
 * request, but a payment form mounted later — after going back to the cart and changing it, say — asks
 * again rather than being shown the total it had before.
 */
export function paymentTotalQueryOptions(isEventBooking: boolean, inCheckIn: boolean, transactionId?: string) {
	return queryOptions({
		queryKey: ['payment-total', transactionId ?? null, isEventBooking, inCheckIn],
		queryFn: () => getPaymentTabTotalAmount(isEventBooking, inCheckIn, transactionId),
		staleTime: 0,
		gcTime: 0,
		// The payment form renders its own failure state with a retry, as it did under useAsync.
		retry: false,
	});
}

/**
 * The payment step's total in useAsync's shape. A change to `confirmationTokenId` or
 * `triggerRecalculation` after mount fetches a fresh total and reports 'loading' until it lands, as
 * useAsync's deps did; so does `refresh`.
 */
export function usePaymentAmount(
	isEventBooking: boolean,
	inCheckIn: boolean,
	transactionId?: string,
	confirmationTokenId?: string,
	triggerRecalculation?: boolean,
): useAsyncResponse<PaymentTabPricesDto | null, unknown> {
	const query = useQuery(paymentTotalQueryOptions(isEventBooking, inCheckIn, transactionId));
	const { refetch } = query;

	// Counts recalculations so only the latest one clears the loading state — an earlier one is
	// cancelled by the next refetch and resolves first.
	const recalculation = useRef(0);
	const [recalculating, setRecalculating] = useState(false);
	const recalculate = () => {
		recalculation.current += 1;
		const current = recalculation.current;
		setRecalculating(true);
		refetch().finally(() => {
			if (current === recalculation.current) {
				setRecalculating(false);
			}
		});
	};

	// Compared against the previous values rather than skipping the first run, so StrictMode's second
	// effect run does not count as a recalculation.
	const previousTriggers = useRef({ confirmationTokenId, triggerRecalculation });
	useEffect(() => {
		const previous = previousTriggers.current;
		if (
			previous.confirmationTokenId === confirmationTokenId &&
			previous.triggerRecalculation === triggerRecalculation
		) {
			return;
		}
		previousTriggers.current = { confirmationTokenId, triggerRecalculation };
		recalculate();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [confirmationTokenId, triggerRecalculation]);

	if (query.isPending || recalculating) {
		return { type: 'loading', error: undefined, data: undefined, refresh: recalculate };
	}
	if (query.isError) {
		return { type: 'error', error: query.error, data: undefined, refresh: recalculate };
	}
	return { type: 'data', error: undefined, data: query.data, refresh: recalculate };
}
